export type ApiMode = 'monolith' | 'split';

export type ApiTarget = 'admin' | 'portal' | 'integration' | 'infrastructure';

export interface ApiUrls {
  admin: string;
  portal: string;
  integration: string;
  infrastructure: string;
}

/**
 * The local split API is fronted by the YARP gateway (CFIK12.AppHost starts it on 7180), so
 * every target resolves to the same origin and the gateway forwards by path prefix. Deployed
 * environments address each App Service directly because there is no gateway in front of them.
 *
 * Requires a trusted ASP.NET dev certificate: `dotnet dev-certs https --trust`.
 */
export const LOCAL_GATEWAY_URL = 'https://localhost:7180';

export const DEV_API_URLS: ApiUrls = {
  admin: 'https://k12-dev-admin.azurewebsites.net',
  portal: 'https://k12-dev-portal.azurewebsites.net',
  integration: 'https://k12-dev-integration.azurewebsites.net',
  infrastructure: 'https://k12-dev-infrastructure.azurewebsites.net',
};

export const TEST_API_URLS: ApiUrls = {
  admin: 'https://k12-test-admin.azurewebsites.net',
  portal: 'https://k12-test-portal.azurewebsites.net',
  integration: 'https://k12-test-integration.azurewebsites.net',
  infrastructure: 'https://k12-test-infrastructure.azurewebsites.net',
};

export const STAGE_API_URLS: ApiUrls = {
  admin: 'https://k12-stage-admin.azurewebsites.net',
  portal: 'https://k12-stage-portal.azurewebsites.net',
  integration: 'https://k12-stage-integration.azurewebsites.net',
  infrastructure: 'https://k12-stage-infrastructure.azurewebsites.net',
};
  
export const PREPROD_API_URLS: ApiUrls = {
  admin: 'https://k12-preprod-admin.azurewebsites.us',
  portal: 'https://k12-preprod-portal.azurewebsites.us',
  integration: 'https://k12-preprod-integration.azurewebsites.us',
  infrastructure: 'https://k12-preprod-infrastructure.azurewebsites.us',
};
export const PROD_API_URLS: ApiUrls = {
  admin: 'https://k12-prod-admin.azurewebsites.us',
  portal: 'https://k12-prod-portal.azurewebsites.us',
  integration: 'https://k12-prod-integration.azurewebsites.us',
  infrastructure: 'https://k12-prod-infrastructure.azurewebsites.us',
};

/**
 * Points every API target at a single origin: the CFIK12.Gateway reverse proxy.
 *
 * Behind the gateway the UI no longer needs to know which backend owns which path — the
 * gateway does, from a route table generated by the same script that generates
 * `api-route-prefixes.ts`. `resolveRewrittenUrl` keeps working unchanged because every
 * target resolves to the same base, so switching costs one line per environment:
 *
 *   apiUrls: gatewayApiUrls('https://k12-dev-gateway.azurewebsites.net'),
 *
 * Once the gateway is the only path in every environment, the whole `api-routing/` layer
 * and this model's per-target `ApiUrls` shape can be deleted. That is deliberately a
 * separate change — see src/CFIK12.Gateway/README.md.
 */
export function gatewayApiUrls(baseUrl: string): ApiUrls {
  const normalized = baseUrl.replace(/\/+$/, '');
  return {
    admin: normalized,
    portal: normalized,
    integration: normalized,
    infrastructure: normalized,
  };
}

/** Local gateway launch profile — see src/CFIK12.Gateway/Properties/launchSettings.json. */
export const LOCAL_GATEWAY_API_URLS: ApiUrls = gatewayApiUrls(LOCAL_GATEWAY_URL);
